/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/JobQaSetupStrip.tsx
 * FILE_ID: JOBHUB-QA-SETUP-STRIP-001
 * PURPOSE: One Set up header and a row of Templates and Quotes.
 * WHY: The three setup columns shared one job. One row keeps each action and
 *      gives the workspace the height when the row is collapsed.
 */
import type { ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@quotech/ui'

export type QaSetupOption = 'templates' | 'quotes'

const OPTIONS: { id: QaSetupOption; label: string }[] = [
  { id: 'templates', label: 'QA using Templates' },
  { id: 'quotes', label: 'QA using Quotes' },
]

export function JobQaSetupStrip({
  collapsed,
  onToggle,
  option,
  onOption,
  forceExpanded = false,
  children,
}: {
  collapsed: boolean
  onToggle: () => void
  option: QaSetupOption | null
  onOption: (next: QaSetupOption) => void
  forceExpanded?: boolean
  children?: ReactNode
}) {
  const open = forceExpanded || !collapsed
  return (
    <section className="w-fit max-w-full rounded-xl border border-border bg-card p-3 md:p-4">
      {!forceExpanded && (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label="Build QA Start/Add/Edit"
          className="flex min-h-[44px] items-center gap-2 text-left md:min-h-0"
        >
          <span className="flex flex-col items-start leading-tight">
            <span className="text-sm font-semibold text-foreground">Build QA</span>
            <span className="text-xs font-medium text-muted-foreground">Start/Add/Edit</span>
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      )}
      {open && (
        <>
          <div className={cn('flex flex-wrap gap-2', !forceExpanded && 'mt-2')} role="group" aria-label="Start/Add/Edit">
            {OPTIONS.map((item) => {
              const active = option === item.id
              return (
                <button
                  key={item.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onOption(item.id)}
                  className={cn(
                    'min-h-[44px] min-w-0 rounded-lg px-3 py-2 text-left text-sm font-semibold md:min-h-0',
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-border text-foreground hover:bg-accent',
                  )}
                >
                  {item.label}
                </button>
              )
            })}
          </div>
          {children}
        </>
      )}
    </section>
  )
}
